import { cn } from '@lib/cn';
import { useId } from 'react';

import type { ReactNode } from 'react';

/**
 * Minimal form controls, styled to match the rest of the shell.
 *
 * Each one owns its `<label htmlFor>` / `id` pairing via `useId` rather than
 * asking the call site for an id. Hand-written ids are the thing that silently
 * stops matching after a copy-paste, and a label that points at nothing costs an
 * operator using a screen reader the field's name entirely.
 */

const CONTROL_CLASSES =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13.5px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-brand focus:ring-2 focus:ring-brand-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500';

function FieldShell({
    id,
    label,
    hint,
    error,
    children,
}: {
    id: string;
    label: string;
    hint?: ReactNode;
    error?: string | undefined;
    children: ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-[12.5px] font-semibold text-gray-700">
                {label}
            </label>
            {children}
            {error !== undefined ? (
                <p id={`${id}-error`} role="alert" className="text-[12px] text-red-700">
                    {error}
                </p>
            ) : (
                hint && (
                    <p id={`${id}-hint`} className="text-[12px] leading-relaxed text-gray-500">
                        {hint}
                    </p>
                )
            )}
        </div>
    );
}

export function TextField({
    label,
    value,
    onChange,
    placeholder,
    hint,
    error,
    disabled = false,
    mono = false,
    autoFocus = false,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    hint?: ReactNode;
    error?: string | undefined;
    disabled?: boolean;
    mono?: boolean;
    autoFocus?: boolean;
}) {
    const id = useId();

    return (
        <FieldShell id={id} label={label} hint={hint} error={error}>
            <input
                id={id}
                type="text"
                value={value}
                disabled={disabled}
                placeholder={placeholder}
                // A marker, not React's `autoFocus`. Inside a `<dialog>` the
                // React prop silently does nothing: it calls `.focus()` during
                // mount, when the element is still `display: none` because
                // `showModal()` has not run yet. `Modal` focuses this after
                // opening, which is the only point at which the field is
                // focusable. See Modal.tsx.
                data-autofocus={autoFocus ? '' : undefined}
                onChange={(event) => onChange(event.target.value)}
                aria-invalid={error !== undefined}
                aria-describedby={
                    error !== undefined ? `${id}-error` : hint ? `${id}-hint` : undefined
                }
                className={cn(CONTROL_CLASSES, mono && 'font-mono text-[13px]')}
            />
        </FieldShell>
    );
}

export function TextAreaField({
    label,
    value,
    onChange,
    placeholder,
    hint,
    error,
    rows = 3,
    disabled = false,
}: {
    label: string;
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    hint?: ReactNode;
    error?: string | undefined;
    rows?: number;
    disabled?: boolean;
}) {
    const id = useId();

    return (
        <FieldShell id={id} label={label} hint={hint} error={error}>
            <textarea
                id={id}
                value={value}
                rows={rows}
                disabled={disabled}
                placeholder={placeholder}
                onChange={(event) => onChange(event.target.value)}
                aria-invalid={error !== undefined}
                aria-describedby={
                    error !== undefined ? `${id}-error` : hint ? `${id}-hint` : undefined
                }
                className={cn(CONTROL_CLASSES, 'resize-y')}
            />
        </FieldShell>
    );
}

export function SelectField<T extends string>({
    label,
    value,
    onChange,
    options,
    hint,
    disabled = false,
}: {
    label: string;
    value: T;
    onChange: (next: T) => void;
    options: readonly { value: T; label: string }[];
    hint?: ReactNode;
    disabled?: boolean;
}) {
    const id = useId();

    return (
        <FieldShell id={id} label={label} hint={hint}>
            <select
                id={id}
                value={value}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value as T)}
                aria-describedby={hint ? `${id}-hint` : undefined}
                className={CONTROL_CLASSES}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </FieldShell>
    );
}
